import { Component, OnDestroy, OnInit } from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { TranslateService } from '@ngx-translate/core'
import { Subscription } from 'rxjs'
import { Datum } from 'src/app/models/common/datum'
import {
  ServicePlanFieldError,
  ServicePlanStepComponent,
} from 'src/app/models/service-plan/service-plan-field-error'
import { ServicePlanStateService } from 'src/app/services/service-plan/service-plan-state.service'

// Step 3 — NDIS pricing classification (story e8-3). Three independent multi-select chip groups
// (Support purpose, Support category, Registration groups), all optional (AC1/AC7) — this step has
// nothing to validate, matching NdisPlanStepComponent's own precedent (isLiveValid intentionally
// omitted, Continue is never blocked here).
@Component({
  selector: 'app-service-plan-ndis-classification-step',
  templateUrl: './ndis-classification-step.component.html',
  styleUrls: ['./ndis-classification-step.component.scss'],
  standalone: false,
})
export class NdisClassificationStepComponent
  implements OnInit, OnDestroy, ServicePlanStepComponent
{
  form!: FormGroup<{
    supportPurpose: FormControl<string[]>
    supportCategory: FormControl<string[]>
    registrationGroups: FormControl<string[]>
  }>

  hintText = ''
  supportPurposeLabel = ''
  supportPurposeOptions: Datum[] = []
  supportCategoryLabel = ''
  supportCategoryOptions: Datum[] = []
  registrationGroupsLabel = ''
  registrationGroupsOptions: Datum[] = []

  private subscription?: Subscription

  constructor(
    private state: ServicePlanStateService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.hintText = this.translate.instant(
      'SERVICE_PLAN.STEP.NDIS_CLASSIFICATION.HINT',
    )
    this.supportPurposeLabel = this.translate.instant(
      'SERVICE_PLAN.STEP.NDIS_CLASSIFICATION.SUPPORT_PURPOSE_LABEL',
    )
    this.supportPurposeOptions = [
      {
        label: this.translate.instant(
          'SERVICE_PLAN.STEP.NDIS_CLASSIFICATION.SUPPORT_PURPOSE_CORE',
        ),
        value: 'core',
      },
      {
        label: this.translate.instant(
          'SERVICE_PLAN.STEP.NDIS_CLASSIFICATION.SUPPORT_PURPOSE_CAPITAL_ASSISTIVE_TECHNOLOGY',
        ),
        value: 'capitalAssistiveTechnology',
      },
      {
        label: this.translate.instant(
          'SERVICE_PLAN.STEP.NDIS_CLASSIFICATION.SUPPORT_PURPOSE_CAPACITY_BUILDING',
        ),
        value: 'capacityBuilding',
      },
    ]

    this.supportCategoryLabel = this.translate.instant(
      'SERVICE_PLAN.STEP.NDIS_CLASSIFICATION.SUPPORT_CATEGORY_LABEL',
    )
    this.supportCategoryOptions = [
      {
        label: this.translate.instant(
          'SERVICE_PLAN.STEP.NDIS_CLASSIFICATION.SUPPORT_CATEGORY_CONSUMABLES',
        ),
        value: 'consumables',
      },
      {
        label: this.translate.instant(
          'SERVICE_PLAN.STEP.NDIS_CLASSIFICATION.SUPPORT_CATEGORY_ASSISTIVE_TECHNOLOGY',
        ),
        value: 'assistiveTechnology',
      },
      {
        label: this.translate.instant(
          'SERVICE_PLAN.STEP.NDIS_CLASSIFICATION.SUPPORT_CATEGORY_IMPROVED_DAILY_LIVING_SKILLS',
        ),
        value: 'improvedDailyLivingSkills',
      },
    ]

    this.registrationGroupsLabel = this.translate.instant(
      'SERVICE_PLAN.STEP.NDIS_CLASSIFICATION.REGISTRATION_GROUPS_LABEL',
    )
    this.registrationGroupsOptions = [
      {
        label: this.translate.instant(
          'SERVICE_PLAN.STEP.NDIS_CLASSIFICATION.REGISTRATION_GROUPS_EARLY_INTERVENTION_SUPPORTS',
        ),
        value: 'earlyInterventionSupports',
      },
      {
        label: this.translate.instant(
          'SERVICE_PLAN.STEP.NDIS_CLASSIFICATION.REGISTRATION_GROUPS_HEARING_EQUIPMENT',
        ),
        value: 'hearingEquipment',
      },
      {
        label: this.translate.instant(
          'SERVICE_PLAN.STEP.NDIS_CLASSIFICATION.REGISTRATION_GROUPS_ASSISTIVE_PRODUCTS',
        ),
        value: 'assistiveProductsForPersonalCareAndSafety',
      },
    ]

    const classification = this.state.getPayload().ndis.classification
    this.form = new FormGroup({
      supportPurpose: new FormControl(classification.supportPurpose, {
        nonNullable: true,
      }),
      supportCategory: new FormControl(classification.supportCategory, {
        nonNullable: true,
      }),
      registrationGroups: new FormControl(classification.registrationGroups, {
        nonNullable: true,
      }),
    })

    this.subscription = this.form.valueChanges.subscribe(() => {
      this.writePayload()
    })
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe()
  }

  // ServicePlanStepComponent contract — none of this step's fields are required (AC1/AC7):
  // Continue is never blocked here, and isLiveValid is intentionally omitted (shell default: not
  // disabled), matching NdisPlanStepComponent's own precedent for a step with nothing to validate.
  isValid(): boolean {
    return true
  }

  getValidationErrors(): ServicePlanFieldError[] {
    return []
  }

  private writePayload(): void {
    const value = this.form.getRawValue()
    const currentNdis = this.state.getPayload().ndis

    this.state.updatePayload({
      ndis: {
        ...currentNdis,
        classification: {
          supportPurpose: value.supportPurpose,
          supportCategory: value.supportCategory,
          registrationGroups: value.registrationGroups,
        },
      },
    })
  }
}
